import {
  Send, CheckCheck, Eye, XCircle, Clock, CheckCircle, Loader, Reply,
} from "lucide-react";
import { pct } from "./constants";

const BroadcastBubble = ({ b }) => {
  const sent    = b.sentMessages      || 0;
  const unread  = b.deliveredMessages || 0;
  const read    = b.readMessages      || 0;
  const replied = b.repliedMessages   || 0;
  const failed  = b.failedMessages    || 0;
  const total   = b.totalMessages     || 0;

  const unreadPct  = pct(unread, sent);
  const readPct    = pct(read, sent);
  const repliedPct = pct(replied, sent);
  const failedPct  = pct(failed, total);

  const STATUS_ICON = {
    completed: <CheckCircle className="w-3 h-3 text-green-500" />,
    sending:   <Loader className="w-3 h-3 text-blue-500 animate-spin" />,
    failed:    <XCircle className="w-3 h-3 text-red-400" />,
    pending:   <Clock className="w-3 h-3 text-yellow-500" />,
  };

  const date = new Date(b.createdAt).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });

  return (
    <div className="flex justify-end mb-3">
      <div className="bg-[#d9fdd3] rounded-xl rounded-tr-sm px-3 py-2 shadow-sm max-w-[280px] w-full">
        {/* Template name + status */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-gray-800 truncate">
              {b.name || b.template?.templateName || "—"}
            </p>
            {b.name && (
              <p className="text-[11px] text-gray-500 truncate">{b.template?.templateName}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
            {STATUS_ICON[b.status] || STATUS_ICON.pending}
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-1.5">
          {/* Sent */}
          <div className="flex items-center gap-2">
            <Send className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div className="h-full bg-gray-400 rounded-full" style={{ width: "100%" }} />
            </div>
            <span className="text-[11px] text-gray-500 w-16 text-right whitespace-nowrap">
              {sent}/{total} sent
            </span>
          </div>

          {/* Unread (delivered but not yet read) */}
          {unread > 0 && (
            <div className="flex items-center gap-2">
              <CheckCheck className="w-3 h-3 text-orange-400 flex-shrink-0" />
              <div className="flex-1 bg-orange-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-orange-300 rounded-full transition-all" style={{ width: `${unreadPct}%` }} />
              </div>
              <span className="text-[11px] text-orange-500 w-16 text-right whitespace-nowrap">
                {unread} ({unreadPct}%)
              </span>
            </div>
          )}

          {/* Read */}
          {read > 0 && (
            <div className="flex items-center gap-2">
              <Eye className="w-3 h-3 text-purple-500 flex-shrink-0" />
              <div className="flex-1 bg-purple-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-purple-400 rounded-full transition-all" style={{ width: `${readPct}%` }} />
              </div>
              <span className="text-[11px] text-purple-600 w-16 text-right whitespace-nowrap">
                {read} ({readPct}%)
              </span>
            </div>
          )}

          {/* Replied by */}
          {replied > 0 && (
            <div className="flex items-center gap-2">
              <Reply className="w-3 h-3 text-teal-500 flex-shrink-0" />
              <div className="flex-1 bg-teal-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-teal-400 rounded-full transition-all" style={{ width: `${repliedPct}%` }} />
              </div>
              <span className="text-[11px] text-teal-600 w-16 text-right whitespace-nowrap">
                {replied} ({repliedPct}%)
              </span>
            </div>
          )}

          {/* Failed */}
          {failed > 0 && (
            <div className="flex items-center gap-2">
              <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
              <div className="flex-1 bg-red-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-red-400 rounded-full transition-all" style={{ width: `${failedPct}%` }} />
              </div>
              <span className="text-[11px] text-red-500 w-16 text-right whitespace-nowrap">
                {failed} ({failedPct}%)
              </span>
            </div>
          )}
        </div>

        {/* Date */}
        <p className="text-[10px] text-gray-400 text-right mt-1.5">{date}</p>
      </div>
    </div>
  );
};

export default BroadcastBubble;
